import { AlertCircle, Upload } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useRealEstateSystem } from '../../hooks/useRealEstateSystem';
import type { RealEstate } from '../../types/RealEstate';

type CSVRealEstateProperty = Omit<RealEstate, 'id' | 'position' | 'created_at' | 'updated_at'>;

// Define field mappings to ensure compatibility between CSV and database
const NUMERIC_FIELDS = ['price', 'target_roi', 'surface', 'size', 'latitude', 'longitude'];
const INTEGER_FIELDS = ['rooms', 'bedrooms', 'bathrooms', 'year_built'];
const ARRAY_FIELDS = ['images', 'tags', 'features', 'amenities'];
const REQUIRED_FIELDS = ['title', 'type', 'transaction_type'];
const LEGACY_TO_NEW_FIELDS: Record<string, string> = {
  // Map legacy field names to new field names if needed
  'property_type': 'type'
};

export function RealEstateImporter() {
  const { chatbotPublicId } = useChatbot();
  const { importFromCSV, loading, error } = useRealEstateSystem();
  const [dragActive, setDragActive] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const parseCSV = (content: string): CSVRealEstateProperty[] => {
    try {
      const lines = content.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      
      // Verify required columns
      const missingColumns = REQUIRED_FIELDS.filter(col => 
        !headers.includes(col) && !headers.includes(LEGACY_TO_NEW_FIELDS[col])
      );
      
      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      }

      const properties: CSVRealEstateProperty[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values = line.split(',').map(v => v.trim());
        const property: Record<string, string | number | string[] | undefined> = {};

        headers.forEach((header, index) => {
          if (index >= values.length || !values[index]) return;
          
          // Map legacy field names to new field names if needed
          const fieldName = LEGACY_TO_NEW_FIELDS[header] || header;
          
          if (NUMERIC_FIELDS.includes(fieldName)) {
            property[fieldName] = parseFloat(values[index]);
          } else if (INTEGER_FIELDS.includes(fieldName)) {
            property[fieldName] = parseInt(values[index], 10);
          } else if (ARRAY_FIELDS.includes(fieldName)) {
            property[fieldName] = values[index].split(';').map(t => t.trim());
          } else {
            property[fieldName] = values[index];
          }
        });

        // Set defaults for required fields
        property.status = property.status || 'available';
        property.currency = property.currency || 'EUR';
        property.transaction_type = property.transaction_type || 'sale';
        property.type = property.type || 'apartment';
        
        // Cast to the expected type
        properties.push(property as unknown as CSVRealEstateProperty);
      }

      return properties;
    } catch (err) {
      throw new Error(`Error parsing CSV file: ${err instanceof Error ? err.message : 'Invalid format'}`);
    }
  };

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!chatbotPublicId || !e.target.files || e.target.files.length === 0) return;
    setParseError(null);

    const file = e.target.files[0];
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setParseError('File must be in CSV format');
      return;
    }

    try {
      const content = await file.text();
      const properties = parseCSV(content);
      
      if (properties.length === 0) {
        setParseError('No valid properties found in the CSV file');
        return;
      }

      // Map properties to match the database schema
      const mappedProperties = properties.map(prop => ({
        ...prop,
        chatbot_public_id: chatbotPublicId,
      }));

      await importFromCSV(chatbotPublicId, mappedProperties);
      e.target.value = '';
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Error during import');
    }
  }, [chatbotPublicId, importFromCSV]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setParseError(null);

    if (!chatbotPublicId || !e.dataTransfer.files || e.dataTransfer.files.length === 0) return;

    const file = e.dataTransfer.files[0];
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setParseError('File must be in CSV format');
      return;
    }

    try {
      const content = await file.text();
      const properties = parseCSV(content);
      
      if (properties.length === 0) {
        setParseError('No valid properties found in the CSV file');
        return;
      }

      // Map properties to match the database schema
      const mappedProperties = properties.map(prop => ({
        ...prop,
        chatbot_public_id: chatbotPublicId,
      }));

      await importFromCSV(chatbotPublicId, mappedProperties);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Error during import');
    }
  }, [chatbotPublicId, importFromCSV]);

  return (
    <div className="bg-white/5 backdrop-blur-xl backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <h3 className="text-lg font-semibold text-white mb-6">Import properties</h3>

      <div className="space-y-4">
        {(error || parseError) && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <p className="text-sm">{error || parseError}</p>
          </div>
        )}

        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-8 transition-colors ${
            dragActive
              ? 'border-indigo-500 bg-indigo-500/10'
              : 'border-white/10 hover:border-indigo-500/50'
          }`}
        >
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={loading.import}
          />

          <div className="text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-400">
              Drag and drop your CSV file here or click to select
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Expected format: title, type, transaction_type, price (optional), surface (optional), rooms (optional), description (optional), etc.
            </p>
          </div>

          {loading.import && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-xl">
              <div className="text-white text-sm flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Import in progress...
              </div>
            </div>
          )}
        </div>

        {/* Exemple de template */}
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">CSV file format</h4>
          <pre className="text-xs text-gray-400 overflow-x-auto">
            title,type,transaction_type,price,currency,surface,rooms,bedrooms,bathrooms,location,address,postal_code,city,country,status,category,subcategory,tags,features,images{'\n'}
            "Appartement T3 Centre-Ville","apartment","sale",250000,EUR,75,3,2,1,"Centre","123 rue Example","75001","Paris","France","available","Appartement","Centre ville","moderne;rénové","climatisation;parking","https://example.com/image1.jpg;https://example.com/image2.jpg"
          </pre>
        </div>
      </div>
    </div>
  );
}